// src/components/Dropdown.js
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search } from 'lucide-react';

const Dropdown = ({ 
  label, 
  value, 
  onChange, 
  options, 
  loading = false, 
  placeholder = "Select an option",
  disabled = false,
  error = null,
  multiSelect = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hoveredOption, setHoveredOption] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (!loading && !disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm('');
      }
    }
  };

  const handleOptionSelect = (option) => {
    if (multiSelect) {
      // Multi-select mode: toggle option in array
      const currentValues = Array.isArray(value) ? value : (value ? [value] : []);
      const newValues = currentValues.includes(option)
        ? currentValues.filter(v => v !== option)
        : [...currentValues, option];
      onChange(newValues);
      // Don't close dropdown in multi-select mode
    } else {
      // Single-select mode
      onChange(option);
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const handleMouseEnter = (event, option) => {
    setHoveredOption(option);
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPos({
      top: rect.top,
      left: rect.right + 10
    });
  };

  const handleMouseLeave = () => {
    setHoveredOption(null);
  };

  const handleClear = () => {
    onChange(multiSelect ? [] : '');
    setIsOpen(false);
    setSearchTerm('');
  };

  // Filter options based on search term
  const filteredOptions = options?.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Debug log for customer entity dropdown
  // if (label === "Customer account number" && process.env.NODE_ENV === 'development') {
  //   console.log('Dropdown render:', {
  //     label,
  //     value,
  //     optionsLength: options?.length,
  //     options: options,
  //     searchTerm,
  //     filteredOptionsLength: filteredOptions.length,
  //     filteredOptions: filteredOptions,
  //     multiSelect
  //   });
  // }

  const displayValue = multiSelect 
    ? (Array.isArray(value) && value.length > 0 
        ? `${value.length} selected` 
        : placeholder)
    : (value || placeholder);
  const hasValue = multiSelect 
    ? (Array.isArray(value) && value.length > 0)
    : Boolean(value);

  return (
    <div className="relative mb-4" ref={dropdownRef}>
      <label className="block text-sm text-gray-700 mb-1">
        {label}
      </label>
      
      <button
        type="button"
        className={`w-full bg-white border px-3 py-2 text-left focus:outline-none focus:ring-1 flex items-center justify-between transition-colors
          ${error 
            ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
            : 'border-gray-300 focus:ring-black focus:border-electric-blue'
          }
          ${disabled || loading 
            ? 'bg-gray-50 cursor-not-allowed' 
            : 'hover:border-gray-400 cursor-pointer'
          }
        `}
        onClick={handleToggle}
        disabled={disabled || loading}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className={`truncate ${hasValue ? "text-gray-900" : "text-gray-500"}`}>
          {loading ? "Loading..." : displayValue}
        </span>
        
        <ChevronDown 
          className={`h-4 w-4 flex-shrink-0 transition-transform duration-200
            ${isOpen ? 'rotate-180' : ''} 
            ${loading ? 'animate-spin' : ''}
            ${disabled ? 'text-n5' : 'text-gray-600'}
          `} 
        />
      </button>
      
      {/* Error message */}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
      
      {/* Dropdown menu */}
      {isOpen && !loading && !disabled && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 shadow-lg max-h-60 overflow-hidden">
          {/* Search input */}
          {options && options.length > 10 && (
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 focus:outline-none focus:ring-1 focus:ring-black focus:border-electric-blue"
                  onClick={(e) => e.stopPropagation()}
                />
                <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-n5" />
              </div>
            </div>
          )}
          
          <div className="overflow-y-auto max-h-52">
            {/* Clear option */}
            {hasValue && (
              <>
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left hover:bg-gray-100 text-gray-500 border-b border-gray-100"
                  onClick={handleClear}
                >
                  <span className="italic">Clear selection</span>
                </button>
              </>
            )}
            
            {/* Show placeholder option if no value selected */}
            {!hasValue && !searchTerm && (
              <div className="px-3 py-2 text-gray-500 bg-gray-50 border-b border-gray-100">
                <span className="italic">{placeholder}</span>
              </div>
            )}
            
            {/* Options */}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const isSelected = multiSelect 
                  ? (Array.isArray(value) && value.includes(option))
                  : (value === option);
                
                return (
                  <div key={index} className="relative">
                    <button
                      type="button"
                      className={`w-full px-3 py-2 text-left hover:bg-blue-50 text-gray-900 transition-all duration-150 flex items-center gap-2 group
                        ${isSelected ? 'bg-gray-100 border-l-4 border-electric-blue' : 'border-l-4 border-transparent'}
                      `}
                      onClick={() => handleOptionSelect(option)}
                      onMouseEnter={(e) => handleMouseEnter(e, option)}
                      onMouseLeave={handleMouseLeave}
                      role="option"
                      aria-selected={isSelected}
                    >
                      {multiSelect && (
                        <input 
                          type="checkbox" 
                          checked={isSelected} 
                          readOnly
                          className="h-4 w-4"
                        />
                      )}
                      <span className="truncate flex-1">{option}</span>
                      {/* <span className="text-n5 text-xs group-hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity">
                        Hover for details →
                      </span> */}
                    </button>
                    
                    {/* Tooltip on hover */}
                    {hoveredOption === option && (
                      <div 
                        className="fixed bg-gray-900 text-white px-4 py-3 shadow-xl max-w-xs text-sm z-50 pointer-events-none"
                        style={{
                          top: `${tooltipPos.top}px`,
                          left: `${tooltipPos.left}px`,
                          maxWidth: '350px',
                          wordWrap: 'break-word'
                        }}
                      >
                        {/* <div className=" mb-1">Full Information:</div> */}
                        <div className="text-gray-100 break-words">{option}</div>
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1.5 text-gray-900">
                          <div className="border-8 border-transparent border-r-gray-900"></div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : searchTerm ? (
              <div className="px-3 py-2 text-gray-500 italic">
                No matches found for "{searchTerm}"
              </div>
            ) : options && options.length === 0 ? (
              <div className="px-3 py-2 text-gray-500 italic">
                No options available
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dropdown;